import dynamic from 'next/dynamic';
import { ComponentProps, useEffect } from 'react';
import use3dMap from '../../hooks/use3dMap';
import useBaseStyle from '../../hooks/useBaseStyle';
import useBaseStyleOverrides from '../../hooks/useBaseStyleOverrides';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateSupportedDynamicStyleIds } from '../../redux/reducer';
import { BaseStyleId, DynamicStyleId } from '../../types/Styles';
import { defaultSupportedDynamicStyleIds, mapId } from '../../utils/constants';
import type BaseMapReactType from './BaseMapReact';

const BaseMapReact = dynamic(() => import('./BaseMapReact'), { ssr: false });

type BaseMapProps = Omit<
  ComponentProps<typeof BaseMapReactType>,
  'baseMapStyle' | 'cursor' | 'fog' | 'id' | 'is3dActive' | 'staticStyleIds' | 'terrain'
> & {
  disableDefaultAttribution?: boolean;
  ignoreInitial3dPitch?: boolean;
  /** When provided, pins the base style to this id on mount and skips all other overrides (e.g. location pages staying on alltrailsOutdoorsV2). */
  initialBaseStyleId?: BaseStyleId;
  supportedDynamicStyleIds?: DynamicStyleId[];
};

/**
 * BaseMap represents our standard customization around our 3rd party map
 * vendor's Map API. We set our typical settings, add our controls, and handle
 * complex functionality specific to maps here and in the appropriate
 * sub-components.
 *
 * @param {props}: BaseMapProps
 * @returns
 */

const BaseMap = ({
  children,
  controlsConfig,
  disableDefaultAttribution,
  ignoreInitial3dPitch,
  initialBaseStyleId,
  supportedDynamicStyleIds = defaultSupportedDynamicStyleIds,
  ...props
}: BaseMapProps) => {
  const dispatch = useAppDispatch();
  const { cursor, is3dActive, staticStyleIds } = useAppSelector(state => ({
    cursor: state.map.cursor,
    is3dActive: state.map.is3dActive,
    staticStyleIds: state.map.staticStyleIds
  }));

  useBaseStyleOverrides({ initialBaseStyleId });
  const baseMapStyle = useBaseStyle();
  const { styles: map3DBaseStyles } = use3dMap(is3dActive, ignoreInitial3dPitch);

  useEffect(() => {
    if (supportedDynamicStyleIds) {
      dispatch(updateSupportedDynamicStyleIds(supportedDynamicStyleIds));
    }
  }, [dispatch, supportedDynamicStyleIds]);

  return (
    <>
      {/* don't render the map until we have our base styles from mapbox 
      we need the glyphs from the style in order to render the cluster marker text
      */}
      {baseMapStyle && (
        <BaseMapReact
          {...props}
          baseMapStyle={baseMapStyle}
          cursor={cursor}
          disableDefaultAttribution={disableDefaultAttribution}
          id={mapId}
          is3dActive={is3dActive}
          staticStyleIds={staticStyleIds}
          {...map3DBaseStyles}
          controlsConfig={controlsConfig}
        >
          {children}
        </BaseMapReact>
      )}
    </>
  );
};

export default BaseMap;
